import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Mail, BookOpen, UserCheck, AlertCircle, Scale, Copyright, ShieldAlert, Activity, XCircle, Gavel, FileText, Clock } from 'lucide-react';

const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};

export function TermsOfUsePage() {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const handleContactClick = (e: React.MouseEvent) => {
        e.preventDefault();
        navigate('/');
        setTimeout(() => {
            const section = document.getElementById('contact-section');
            if (section) {
                section.scrollIntoView({ behavior: 'smooth' });
            }
        }, 150);
    };

    const sections = [
        { key: 'definitions', icon: BookOpen },
        { key: 'eligibility', icon: UserCheck },
        { key: 'userResponsibilities', icon: AlertCircle },
        { key: 'conductRules', icon: Scale },
        { key: 'intellectualProperty', icon: Copyright },
        { key: 'limitationOfLiability', icon: ShieldAlert },
        { key: 'serviceAvailability', icon: Activity },
        { key: 'termination', icon: XCircle },
        { key: 'applicableLaw', icon: Gavel },
        { key: 'changes', icon: FileText }
    ];

    return (
        <article className="pb-10">
            {/* Header */}
            <header className="mb-14 text-center">
                <div className="inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-full bg-brand-500/10 border border-brand-500/20 text-brand-700 dark:text-brand-300 text-sm font-medium mb-6">
                    <Lock size={16} />
                    <span>{t('legal.badge', 'Conformidade Legal')}</span>
                </div>
                <h1>{t('legal.terms.title')}</h1>
                <p className="text-muted-foreground text-lg sm:text-[1.1rem] leading-relaxed mt-6 text-justify">
                    {t('legal.terms.introduction')}
                </p>
                <div className="mt-8 flex items-center justify-center gap-2 text-sm text-muted-foreground/80 bg-background/50 inline-flex px-4 py-2 rounded-lg border border-border/50">
                    <Clock size={16} />
                    <span className='text-left'>{t('legal.terms.lastUpdated')}</span>
                </div>
            </header>

            {/* Sections */}
            <div className="space-y-16">
                {sections.map(({ key, icon: Icon }, index) => (
                    <motion.section
                        key={key}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-100px" }}
                        variants={fadeInUp}
                        className="relative"
                    >
                        {/* Connecting line for all but the last item */}
                        {index !== sections.length - 1 && (
                            <div className="absolute left-6 top-16 bottom-[-4rem] w-[2px] bg-gradient-to-b from-border to-transparent hidden sm:block" />
                        )}

                        <div className="flex items-start gap-4 mb-6">
                            <div className="w-12 h-12 flex-shrink-0 rounded-2xl bg-gradient-to-br from-brand-500/10 to-transparent flex items-center justify-center text-brand-600 dark:text-brand-400 border border-brand-500/20 shadow-sm relative z-10 bg-background">
                                <Icon size={22} strokeWidth={1.5} />
                            </div>
                            <h2 className="!my-0 !border-none !pb-0 flex-1 text-2xl sm:text-[1.65rem] leading-tight mt-1.5">{t(`legal.terms.sections.${key}.title`)}</h2>
                        </div>
                        <div className="sm:pl-16">
                            <p className="text-muted-foreground leading-relaxed text-[1.05rem] text-justify m-0">
                                {t(`legal.terms.sections.${key}.content`)}
                            </p>
                        </div>
                    </motion.section>
                ))}
            </div>

            {/* Contact Footer Banner */}
            <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                className="mt-20 p-8 sm:p-10 rounded-[2rem] bg-card/50 dark:bg-[#1C1714]/80 backdrop-blur-sm border border-border/60 shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-6 relative overflow-hidden group"
            >
                {/* Decorative background glow */}
                <div className="absolute top-0 right-0 -mr-8 -mt-8 w-64 h-64 rounded-full bg-brand-500/5 blur-3xl pointer-events-none" />
                <div className="absolute bottom-0 left-0 -ml-8 -mb-8 w-64 h-64 rounded-full bg-brand-500/5 blur-3xl pointer-events-none" />

                <div className="text-left relative z-10 flex-1 w-full">
                    <h3 className="!my-0 text-2xl font-display font-semibold mb-2">{t('legal.privacy.contact.title')}</h3>
                    <p className="text-muted-foreground !my-0 text-[1.1rem]">{t('legal.privacy.contact.description')}</p>
                </div>
                <button
                    onClick={handleContactClick}
                    className="!text-white !no-underline transition-all duration-300 bg-[#6F4E37] hover:bg-[#5A3E2A] px-8 py-4 rounded-xl font-semibold shadow flex items-center justify-center gap-2 whitespace-nowrap shrink-0 w-full md:w-auto relative z-10 active:scale-[0.98]"
                >
                    <Mail size={18} />
                    {t('legal.privacy.contact.button')}
                </button>
            </motion.div>
        </article>
    );
}
